/**
 * @fileoverview Ads performance over time chart using ShadCn Charts (Recharts) and derived dashboard data
 * @module shared/components/ui/AdsPerformanceOverTimeChart
 */

import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import { pl, enUS } from 'date-fns/locale'
import { Bar, CartesianGrid, ComposedChart, LabelList, XAxis, YAxis } from 'recharts'
import { cn } from '@/shared/lib/utils'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import { Card } from '@/shared/components/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useRevenueVsCostsSeries } from '@/shared/hooks/dashboard/useRevenueVsCostsSeries'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { useFilter } from '@/shared/hooks/useFilter'
import { formatIsoWeekRange, isDateRangeLessThanMonth, isIsoWeekPeriod } from '@/shared/utils/dateUtils'
import { adaptiveMaxBarSize, computeLabeledBarIndices } from '@/shared/utils/chartBars'
import { useProductGroupsMetadata } from '@/shared/hooks'
import { Thumbnail } from './Thumbnail'
import { ChartEmptyState } from './ChartEmptyState'
import { useTopRevenueByPeriod, useTopCountryRevenueByPeriod } from '@/shared/hooks/dashboard'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { codeToFlag } from '@/shared/utils/countryUtils'
import { useIntegrationsStatus } from '@/features/settings/hooks/useIntegrationsStatus'
import { RechartsHoverAdapter } from '@/shared/components/ui/RechartsHoverAdapter'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Globe, Megaphone, Package } from 'lucide-react'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'

interface RevenueCostsPoint {
  period: string
  revenue: number
  adSpend: number
}

interface AdsPerformanceOverTimeChartProps {
  className?: string
}

function calcTotals(data: RevenueCostsPoint[]): { revenue: number; adSpend: number } {
  return data.reduce(
    (acc, d) => {
      acc.revenue += d.revenue
      acc.adSpend += d.adSpend
      return acc
    },
    { revenue: 0, adSpend: 0 }
  )
}

interface AdsPerformanceLegendProps {
  revenueLabel: string
  adSpendLabel: string
  totals: { revenue: number; adSpend: number }
  formatValue: (value: number) => string
}

function AdsPerformanceLegend({
  revenueLabel,
  adSpendLabel,
  totals,
  formatValue,
}: AdsPerformanceLegendProps): ReactElement {
  return (
    <div className="flex items-center justify-end gap-2 text-xs sm:gap-4 sm:text-sm">
      <div className="flex items-center gap-1.5">
        <div
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: 'var(--color-chart-legend-revenue)' }}
        />
        <span className="whitespace-nowrap text-muted-foreground">{revenueLabel}</span>
        <span className="whitespace-nowrap font-mono font-semibold text-foreground">
          {formatValue(totals.revenue)}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <div
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: 'var(--color-chart-legend-adspend)' }}
        />
        <span className="whitespace-nowrap text-muted-foreground">{adSpendLabel}</span>
        <span className="whitespace-nowrap font-mono font-semibold text-foreground">
          {formatValue(totals.adSpend)}
        </span>
      </div>
    </div>
  )
}

/**
 * Chart configuration for ShadCn charts
 */
const createChartConfig = (t: TFunction<'features-home'>): ChartConfig => ({
  revenue: {
    label: t('metricLabels.totalRevenue'),
    color: 'var(--color-chart-legend-revenue)',
  },
  adSpend: {
    label: t('metricLabels.adSpend'),
    color: 'var(--color-chart-legend-adspend)',
  },
})

/**
 * Formats a period string to a more readable date format
 * @param period - Period string (e.g., "2025-08-26")
 * @param locale - Current locale ('en' or 'pl')
 * @returns Formatted date string (e.g., "26 Aug 2025" or "26 sie 2025")
 */
function formatPeriod(period: string, locale: string = 'en', includeYear: boolean = true): string {
  try {
    // Try to parse as ISO date first
    const date = parseISO(period)
    const dateLocale = locale === 'pl' ? pl : enUS
    return format(date, includeYear ? 'd MMM yyyy' : 'd MMM', { locale: dateLocale })
  } catch {
    // If not a valid date, return the original period
    return period
  }
}

/**
 * Product/country item for the tooltip
 */
interface TooltipItem {
  key: string
  name: string
  icon: ReactElement
  revenue: number
  adSpend: number
}

/**
 * Props for AdsTooltipContent - the shared tooltip table content
 */
interface AdsTooltipContentProps {
  /** Period string (e.g., "2025-08-26") */
  period: string
  /** View mode - products or countries */
  viewMode: 'products' | 'countries'
  /** Function to get top revenue products for a period */
  topRevenueFor: (period: string, limit: number) => { sku: string; name: string; revenue: number; adSpend: number }[]
  /** Function to get top revenue countries for a period */
  topCountryRevenueFor: (period: string, limit: number) => { country: string; name: string; revenue: number; adSpend: number }[]
  /** Function to get total revenue for a period (products view) */
  totalRevenueFor: (period: string) => number
  /** Function to get total ad spend for a period (products view) */
  totalAdSpendFor: (period: string) => number
  /** Function to get total revenue for a period (countries view) */
  totalCountryRevenueFor: (period: string) => number
  /** Function to get total ad spend for a period (countries view) */
  totalCountryAdSpendFor: (period: string) => number
  /** Function to get product group metadata */
  getGroup: (key: string) => { imageUrl?: string | null } | undefined
  /** Function to render country icon */
  renderCountryIcon: (code: string, name: string) => ReactElement
  /** Function to format money values */
  compactMoney: (value: number) => string
}

/**
 * Reusable tooltip content that displays top revenue products/countries for a period.
 * Used by both desktop hover tooltip and mobile bottom sheet.
 */
function AdsTooltipContent({
  period,
  viewMode,
  topRevenueFor,
  topCountryRevenueFor,
  totalRevenueFor,
  totalAdSpendFor,
  totalCountryRevenueFor,
  totalCountryAdSpendFor,
  getGroup,
  renderCountryIcon,
  compactMoney,
}: AdsTooltipContentProps): ReactElement {
  const { t, i18n } = useTranslation('features-home')

  // Weekly buckets arrive as ISO week keys (e.g. "2026-W21"); show the full
  // Mon–Sun range instead of a single ambiguous date.
  const periodLabel = isIsoWeekPeriod(period)
    ? formatIsoWeekRange(period, i18n.language)
    : formatPeriod(period, i18n.language, false)

  const items: TooltipItem[] = viewMode === 'countries'
    ? topCountryRevenueFor(period, 6).map(c => ({
        key: c.country,
        name: c.name,
        icon: renderCountryIcon(c.country, c.name),
        revenue: c.revenue,
        adSpend: c.adSpend,
      }))
    : topRevenueFor(period, 6).map(p => {
        const group = getGroup(p.sku)
        const img = group?.imageUrl ?? null
        return {
          key: p.sku,
          name: p.name,
          icon: <Thumbnail src={img} alt={p.name} size={40} />,
          revenue: p.revenue,
          adSpend: p.adSpend,
        }
      })

  const totalRevenue = viewMode === 'countries'
    ? totalCountryRevenueFor(period)
    : totalRevenueFor(period)
  const totalAdSpend = viewMode === 'countries'
    ? totalCountryAdSpendFor(period)
    : totalAdSpendFor(period)

  if (!items.length) {
    return (
      <div className="text-base font-medium text-center p-4 text-muted-foreground">
        {t('charts.adsPerformance.noDataOn', {
          defaultValue: 'No data on {{date}}',
          date: periodLabel,
        })}
      </div>
    )
  }

  return (
    <>
      {/* table-fixed so long product names can't widen the table past the
          hovercard's fixed width — the name column gets the leftover space
          and wraps instead. */}
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent border-t-0 bg-black/10">
            <TableHead className="px-4 h-8">
              {/* Naming the mode here closes the loop with the header toggle —
                  the first hover shows what Products/Countries switches. */}
              <span className="text-xs font-medium text-muted-foreground">
                {periodLabel}
                {' — '}
                {viewMode === 'countries'
                  ? t('charts.adsPerformance.topCountries')
                  : t('charts.adsPerformance.topProducts')}
              </span>
            </TableHead>
            <TableHead className="w-20 px-2 h-8 text-right">
              <div className="flex items-center justify-end gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: 'var(--color-chart-legend-revenue)' }}
                />
                <span className="text-xs font-medium text-muted-foreground">
                  {t('metricLabels.totalRevenue')}
                </span>
              </div>
            </TableHead>
            <TableHead className="w-20 px-4 h-8 text-right">
              <div className="flex items-center justify-end gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: 'var(--color-chart-legend-adspend)' }}
                />
                <span className="text-xs font-medium text-muted-foreground">
                  {t('metricLabels.adSpend')}
                </span>
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.key} className="hover:bg-transparent">
              <TableCell className="px-3 py-2 align-middle">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="shrink-0">{item.icon}</span>
                  <span className="min-w-0 line-clamp-2 break-words text-xs text-muted-foreground">{item.name}</span>
                </div>
              </TableCell>
              <TableCell className="w-18 px-2 py-2 text-right align-middle">
                <span className="text-xs font-medium tabular-nums text-foreground whitespace-nowrap">
                  {compactMoney(Math.round(item.revenue))}
                </span>
              </TableCell>
              <TableCell className="w-18 px-3 py-2 text-right align-middle">
                <span className="text-xs font-medium tabular-nums text-muted-foreground/70 whitespace-nowrap">
                  {compactMoney(Math.round(item.adSpend))}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="px-4 py-3 border-t border-border space-y-0.5">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {t('metricLabels.totalRevenue')}
          </span>
          <span className="flex items-center gap-1 text-sm font-semibold tabular-nums text-foreground">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: 'var(--color-chart-legend-revenue)' }}
            />
            {compactMoney(Math.round(totalRevenue))}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground opacity-70">
            {t('metricLabels.adSpend')}
          </span>
          <span className="flex items-center gap-1 text-sm font-normal tabular-nums text-muted-foreground opacity-60">
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: 'var(--color-chart-legend-adspend)' }}
            />
            {compactMoney(Math.round(totalAdSpend))}
          </span>
        </div>
      </div>
    </>
  )
}

/**
 * Custom tooltip props
 */
interface AdsTooltipProps {
  active?: boolean
  payload?: { value: number }[]
  label?: string
  viewMode: 'products' | 'countries'
  topRevenueFor: (period: string, limit: number) => { sku: string; name: string; revenue: number; adSpend: number }[]
  topCountryRevenueFor: (period: string, limit: number) => { country: string; name: string; revenue: number; adSpend: number }[]
  totalRevenueFor: (period: string) => number
  totalAdSpendFor: (period: string) => number
  totalCountryRevenueFor: (period: string) => number
  totalCountryAdSpendFor: (period: string) => number
  getGroup: (key: string) => { imageUrl?: string | null } | undefined
  renderCountryIcon: (code: string, name: string) => ReactElement
  compactMoney: (value: number) => string
  /** Whether to skip rendering (for mobile where Sheet is used instead) */
  disabled?: boolean
}

/**
 * Custom tooltip component with table structure using RechartsHoverAdapter
 */
function CustomAdsTooltip(
  props: AdsTooltipProps & { [key: string]: unknown }
): ReactElement | null {
  const {
    viewMode,
    topRevenueFor,
    topCountryRevenueFor,
    totalRevenueFor,
    totalAdSpendFor,
    totalCountryRevenueFor,
    totalCountryAdSpendFor,
    getGroup,
    renderCountryIcon,
    compactMoney,
    disabled = false,
  } = props

  return (
    <RechartsHoverAdapter
      {...props}
      dataChartId="chart-ads-performance-chart"
      width={520}
      estimatedHeight={400}
      followMouse={false}
      disabled={disabled}
    >
      {({ label: tooltipLabel }) => {
        const period = tooltipLabel ? String(tooltipLabel) : ''
        if (!period) return null

        return (
          <AdsTooltipContent
            period={period}
            viewMode={viewMode}
            topRevenueFor={topRevenueFor}
            topCountryRevenueFor={topCountryRevenueFor}
            totalRevenueFor={totalRevenueFor}
            totalAdSpendFor={totalAdSpendFor}
            totalCountryRevenueFor={totalCountryRevenueFor}
            totalCountryAdSpendFor={totalCountryAdSpendFor}
            getGroup={getGroup}
            renderCountryIcon={renderCountryIcon}
            compactMoney={compactMoney}
          />
        )
      }}
    </RechartsHoverAdapter>
  )
}

/**
 * Ads performance chart component that compares revenue and ad spend.
 * It hides itself when the selected date range contains only a single day.
 *
 * @component
 */
export function AdsPerformanceOverTimeChart({
  className,
}: AdsPerformanceOverTimeChartProps): ReactElement {
  const { t, i18n } = useTranslation('features-home')
  const { startDate, endDate, aggregation } = useFilter()
  const { data: series, isLoading: loading, isError, error } = useRevenueVsCostsSeries()
  const { topRevenueFor, totalRevenueFor, totalAdSpendFor } = useTopRevenueByPeriod()
  const {
    topCountryRevenueFor,
    totalRevenueFor: totalCountryRevenueFor,
    totalAdSpendFor: totalCountryAdSpendFor,
  } = useTopCountryRevenueByPeriod()
  const { getGroup } = useProductGroupsMetadata()
  useReportingCurrency() // keep hook ordering consistent; formatting handled by useCompactMoney
  const integrationsQuery = useIntegrationsStatus()

  const compactMoney = useCompactMoney()
  const formatValue = useCallback((value: number): string => compactMoney(value), [compactMoney])
  const chartTitle = t('charts.adsPerformance.title')
  const [viewMode, setViewMode] = useState<'products' | 'countries'>('products')
  const isMobile = useIsMobile()

  // Mobile: track selected period for bottom sheet
  const [mobilePeriod, setMobilePeriod] = useState<string | null>(null)
  const chartContainerRef = useRef<HTMLDivElement>(null)

  const data: RevenueCostsPoint[] = useMemo(() => {
    if (!series || series.length === 0) return []
    return series.map(p => ({
      period: p.period,
      revenue: p.revenue,
      adSpend: p.adSpend,
    }))
  }, [series])

  const totals = useMemo(() => calcTotals(data), [data])
  const chartConfig = useMemo(() => createChartConfig(t), [t])
  // Calculate appropriate tick interval based on data length and aggregation
  const tickInterval = useMemo(() => {
    const dataLength = data.length

    if (aggregation === 'Daily') {
      // For daily data, show fewer ticks if there's a lot of data
      if (dataLength > 20) return Math.ceil(dataLength / 8) // Show ~8 ticks
      if (dataLength > 10) return Math.ceil(dataLength / 6) // Show ~6 ticks
      return Math.ceil(dataLength / 4) // Show ~4 ticks for smaller datasets
    }

    // For other aggregations, show more frequent ticks
    return 0 // Let Recharts decide automatically
  }, [data.length, aggregation])

  // Check if all values are zero to avoid rendering
  const isAllZero = useMemo(() => {
    return data.every(d => d.revenue === 0 && d.adSpend === 0)
  }, [data])

  const maxBarSize = adaptiveMaxBarSize(data.length)

  // Every revenue bar gets a value label, except an immediate neighbor at
  // nearly the same height — the shorter bar's label is skipped.
  const labeledIndices = useMemo(
    () => computeLabeledBarIndices(data.map((d) => d.revenue)),
    [data]
  )

  const renderBarLabel = useCallback(
    (props: unknown): ReactElement | null => {
      const { x, y, width, value, index } = props as {
        x?: number
        y?: number
        width?: number
        value?: number
        index?: number
      }
      if (index === undefined || !labeledIndices.has(index)) return null
      if (typeof value !== 'number' || value <= 0) return null
      if (x === undefined || y === undefined || width === undefined) return null
      return (
        <text
          x={x + width / 2}
          y={y - 8}
          textAnchor="middle"
          fontSize={11}
          fontWeight={600}
          fill="var(--color-chart-legend-revenue)"
        >
          {compactMoney(Math.round(value))}
        </text>
      )
    },
    [labeledIndices, compactMoney]
  )

  // Check if there's no ad spend data (for hiding when no integrations)
  const hasNoAdSpend = useMemo(() => {
    return data.length === 0 || data.every(d => d.adSpend === 0)
  }, [data])

  const renderCountryIcon = useCallback((code: string, name: string): ReactElement => {
    const flag = codeToFlag(code)
    return (
      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-foreground/5 text-lg" aria-label={name}>
        {flag}
      </span>
    )
  }, [])

  // Mobile: handle touch on chart to find nearest data point and open sheet
  const handleMobileTouch = useCallback((clientX: number) => {
    if (!isMobile || !chartContainerRef.current || !data.length) return

    const rect = chartContainerRef.current.getBoundingClientRect()
    const chartLeft = rect.left + 12 // margin.left
    const chartRight = rect.right - 12 // margin.right
    const chartWidth = chartRight - chartLeft

    // Calculate which data point is closest to the touch
    const touchX = clientX - chartLeft
    const ratio = Math.max(0, Math.min(1, touchX / chartWidth))
    const index = Math.round(ratio * (data.length - 1))
    const dataPoint = data[index]

    if (dataPoint) {
      setMobilePeriod(dataPoint.period)
    }
  }, [isMobile, data])

  // Add touch event listener for mobile
  useEffect(() => {
    if (!isMobile) return
    const container = chartContainerRef.current
    if (!container) return

    const handleTouch = (e: TouchEvent): void => {
      const touch = e.touches[0]
      if (e.touches.length === 1 && touch) {
        handleMobileTouch(touch.clientX)
      }
    }

    container.addEventListener('touchstart', handleTouch, { passive: true })
    return () => container.removeEventListener('touchstart', handleTouch)
  }, [isMobile, handleMobileTouch])

  // Early return after all hooks are called
  if (startDate && endDate && differenceInCalendarDays(endDate, startDate) === 0) {
    return <></>
  }

  // Hide when monthly aggregation is selected and date range is less than a month
  // (only 1 collapsed data point, which is shown in metric cards)
  if (aggregation === 'Monthly' && isDateRangeLessThanMonth(startDate, endDate)) {
    return <></>
  }

  // Hide completely when no ad integrations connected and no ad spend data
  const integrations = integrationsQuery.data ?? []
  const facebookStatus = integrations.find(i => i.provider === 'FACEBOOK')
  const googleStatus = integrations.find(i => i.provider === 'GOOGLE_ADS')
  const hasNoAdIntegrations =
    (!facebookStatus || facebookStatus.status === 'NOT_CONFIGURED') &&
    (!googleStatus || googleStatus.status === 'NOT_CONFIGURED')

  if (hasNoAdIntegrations && hasNoAdSpend) {
    return <></>
  }

  if (loading) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {chartTitle}
          </h3>
        </div>
        <div className="pt-6 flex h-chart-lg items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-foreground/20 border-t-foreground" />
        </div>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {chartTitle}
          </h3>
        </div>
        <div className="pt-6 flex h-chart-lg items-center justify-center text-[--color-negative]">
          {error}
        </div>
      </Card>
    )
  }

  if (!data.length || isAllZero) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {chartTitle}
          </h3>
        </div>
        <ChartEmptyState
          variant="area"
          icon={Megaphone}
          message={t('charts.adsPerformance.noData')}
          hint={t('charts.emptyHint')}
          className="mt-6 h-chart-lg"
        />
      </Card>
    )
  }

  return (
    <Card className={cn('rounded-xl border border-border/40 bg-card/30 px-6 py-6', className)}>
      {/* Header section */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{chartTitle}</h3>
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={val => {
              if (val === 'products' || val === 'countries') setViewMode(val)
            }}
            variant="outline"
            size="sm"
            className="gap-0"
            aria-label={t('charts.adsPerformance.toggleLabel')}
          >
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <ToggleGroupItem
                  value="products"
                  className="gap-1.5 px-4 text-sm font-semibold text-foreground/70 data-[state=on]:text-foreground data-[state=on]:bg-foreground/10 data-[state=on]:shadow-inner rounded-l-md"
                  aria-label={t('charts.adsPerformance.products')}
                >
                  <Package className="h-3.5 w-3.5" />
                  {t('charts.adsPerformance.products')}
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                sideOffset={6}
                hideArrow
                className="rounded-lg border border-border/50 !bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] px-3.5 py-2.5 text-sm text-foreground shadow-xl"
              >
                {t('charts.adsPerformance.productsHint')}
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <ToggleGroupItem
                  value="countries"
                  className="gap-1.5 px-4 text-sm font-semibold text-foreground/70 data-[state=on]:text-foreground data-[state=on]:bg-foreground/10 data-[state=on]:shadow-inner rounded-r-md"
                  aria-label={t('charts.adsPerformance.countries')}
                >
                  <Globe className="h-3.5 w-3.5" />
                  {t('charts.adsPerformance.countries')}
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                sideOffset={6}
                hideArrow
                className="rounded-lg border border-border/50 !bg-[oklch(from_var(--glass-surface-dark)_l_c_h/0.95)] px-3.5 py-2.5 text-sm text-foreground shadow-xl"
              >
                {t('charts.adsPerformance.countriesHint')}
              </TooltipContent>
            </Tooltip>
          </ToggleGroup>
        </div>
        <AdsPerformanceLegend
          revenueLabel={t('metricLabels.totalRevenue')}
          adSpendLabel={t('metricLabels.adSpend')}
          totals={totals}
          formatValue={formatValue}
        />
      </div>

      {/* Chart section */}
      <div
        ref={chartContainerRef}
        className="pt-6"
      >
        <ChartContainer config={chartConfig} className="h-chart-lg w-full" id="ads-performance-chart">
          {/* Top margin reserves room for the value label above the tallest
              bar, which reaches dataMax (the top of the plot area). */}
          <ComposedChart
            data={data}
            margin={{
              left: 12,
              right: 12,
              top: 32,
              bottom: 12,
            }}
          >
            <CartesianGrid
              vertical={false}
              horizontal
              stroke="var(--color-muted-foreground)"
              strokeOpacity={0.07}
              strokeDasharray="8 4"
              shapeRendering="crispEdges"
            />
            <XAxis
              dataKey="period"
              tickLine={false}
              axisLine={false}
              tickMargin={13}
              tick={{ fontSize: 13 }}
              tickFormatter={value => formatPeriod(String(value), i18n.language)}
              interval={tickInterval}
            />
            {/* Hidden duplicate axis so the ad-spend bars overlay the
                revenue bars (same slot) instead of sitting beside them. */}
            <XAxis dataKey="period" xAxisId="overlay" hide />
            <YAxis
              orientation="right"
              tickLine={false}
              axisLine={false}
              tickMargin={13}
              tick={{ fontSize: 13, textAnchor: 'start' }}
              tickFormatter={value => {
                return formatValue(value as number)
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <CustomAdsTooltip
                  viewMode={viewMode}
                  topRevenueFor={topRevenueFor}
                  topCountryRevenueFor={topCountryRevenueFor}
                  totalRevenueFor={totalRevenueFor}
                  totalAdSpendFor={totalAdSpendFor}
                  totalCountryRevenueFor={totalCountryRevenueFor}
                  totalCountryAdSpendFor={totalCountryAdSpendFor}
                  getGroup={getGroup}
                  renderCountryIcon={renderCountryIcon}
                  compactMoney={compactMoney}
                  disabled={isMobile}
                />
              }
              wrapperStyle={{ display: 'none' }}
            />
            <defs>
              {/* Lengthwise fade (dim base, full color at the tip) — same
                  treatment as the horizontal profit bars and donut slices. */}
              <linearGradient id="ads-perf-bar-fill" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="var(--color-chart-legend-revenue)" stopOpacity={0.55} />
                <stop offset="100%" stopColor="var(--color-chart-legend-revenue)" stopOpacity={0.9} />
              </linearGradient>
            </defs>
            {/* Revenue as bars: per-period revenue is discrete order totals —
                bars read honestly (zero periods are gaps), unlike an
                interpolated area. */}
            <Bar
              dataKey="revenue"
              fill="url(#ads-perf-bar-fill)"
              radius={[6, 6, 0, 0]}
              maxBarSize={maxBarSize}
              activeBar={{ fill: 'var(--color-chart-legend-revenue)', fillOpacity: 0.9 }}
              isAnimationActive={false}
            >
              <LabelList dataKey="revenue" content={renderBarLabel} />
            </Bar>
            {/* Outline-only so the revenue bar behind stays visible even
                when ad spend exceeds revenue for the period. */}
            <Bar
              xAxisId="overlay"
              dataKey="adSpend"
              fill="transparent"
              stroke="var(--color-chart-legend-adspend)"
              strokeWidth={1.5}
              radius={[6, 6, 0, 0]}
              maxBarSize={maxBarSize}
              activeBar={{ fill: 'var(--color-chart-legend-adspend)', fillOpacity: 0.2 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ChartContainer>
      </div>

      {/* Mobile: Bottom sheet for period details */}
      {isMobile && (
        <Sheet open={!!mobilePeriod} onOpenChange={(open) => !open && setMobilePeriod(null)}>
          <SheetContent
            side="bottom"
            showCloseButton={false}
            className={cn(
              'max-h-[85vh] overflow-y-auto rounded-t-2xl',
              'bg-[var(--glass-surface-dark)]'
            )}
          >
            {/* Hidden title for accessibility */}
            <SheetHeader className="sr-only">
              <SheetTitle>
                {t('charts.adsPerformance.periodDetails', { defaultValue: 'Period Details' })}
              </SheetTitle>
              <SheetDescription>
                {t('charts.adsPerformance.periodDetailsDescription', {
                  defaultValue: 'Revenue and ad spend breakdown for the selected period',
                })}
              </SheetDescription>
            </SheetHeader>
            <div className="pb-safe">
              {mobilePeriod && (
                <AdsTooltipContent
                  period={mobilePeriod}
                  viewMode={viewMode}
                  topRevenueFor={topRevenueFor}
                  topCountryRevenueFor={topCountryRevenueFor}
                  totalRevenueFor={totalRevenueFor}
                  totalAdSpendFor={totalAdSpendFor}
                  totalCountryRevenueFor={totalCountryRevenueFor}
                  totalCountryAdSpendFor={totalCountryAdSpendFor}
                  getGroup={getGroup}
                  renderCountryIcon={renderCountryIcon}
                  compactMoney={compactMoney}
                />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </Card>
  )
}
